import {createNestablePublicClientApplication, PublicClientApplication} from "@azure/msal-browser";
import {AccessScope, AzureAuthorityUrl, AzureClientId, DevAccessScope, isDev} from "./config";

const signIn = async (
    setToken: (x: string) => void,
    displayError: (x: string) => void) => {

    let loginDialog: Office.Dialog;
    const dialogLoginUrl = `https://${location.hostname}${location.port ? ':' + location.port : ''}/login/login.html`;

    const displayDialogAsync = () => new Promise<void>((resolve, reject) => {

        const processLoginMessage = (arg: {message: string, origin: string}) => {
            let messageFromDialog = JSON.parse(arg.message);
            if (messageFromDialog.status === 'success') {
                console.log('success!', arg.message, messageFromDialog.result);
                // We now have a valid access token.
                loginDialog.close();
                setToken(messageFromDialog.result);
                resolve();
            }
            else {
                // Something went wrong with authentication or the authorization of the web application.
                loginDialog.close();
                displayError(messageFromDialog.result);
                reject();
            }
        };

        const processLoginDialogEvent = (arg) => {
            processDialogEvent(arg, displayError);
            reject();
        };

        const processDialogEvent = (arg: {error: number, type: string},
                                    displayError: (x: string) => void) => {

            switch (arg.error) {
                case 12002:
                    displayError('The dialog box has been directed to a page that it cannot find or load, or the URL syntax is invalid.');
                    break;
                case 12003:
                    displayError('The dialog box has been directed to a URL with the HTTP protocol. HTTPS is required.');
                    break;
                case 12006:
                    // 12006 means that the user closed the dialog instead of waiting for it to close.
                    // It is not known if the user completed the login or logout, so assume the user is
                    // logged out and revert to the app's starting state. It does no harm for a user to
                    // press the login button again even if the user is logged in.
                    break;
                default:
                    displayError('Unknown error in dialog box.');
                    break;
            }
        };

        Office.context.ui.displayDialogAsync(
            dialogLoginUrl,
            {height: 40, width: 30},
            (result) => {
                if (result.status === Office.AsyncResultStatus.Failed) {
                    displayError(`${result.error.code} ${result.error.message}`);
                } else {
                    loginDialog = result.value;
                    loginDialog.addEventHandler(Office.EventType.DialogMessageReceived, processLoginMessage);
                    loginDialog.addEventHandler(Office.EventType.DialogEventReceived, processLoginDialogEvent);
                }
            }
        );
    });

    await displayDialogAsync();
}

export let nestedAppAuthSupported = false;
export let nestablePublicClientApplication;

export async function initAuth() {
    nestedAppAuthSupported = Office.context.requirements.isSetSupported("NestedAppAuth");
    if (nestedAppAuthSupported) {
        nestablePublicClientApplication = await createNestablePublicClientApplication({
            auth: {
                clientId: AzureClientId,
                authority: AzureAuthorityUrl,
            }
        });
    }
}

export async function getAccessToken2() {
    if (nestedAppAuthSupported) {
        let accessToken = null;
        const tokenRequest = {
            scopes: [isDev ? DevAccessScope : AccessScope ],
        }
        if (nestablePublicClientApplication) {
            try {
                // console.log("Trying to acquire token silently...");
                const userAccount = await nestablePublicClientApplication.acquireTokenSilent(tokenRequest);
                // console.log("Acquired token silently.");
                accessToken = userAccount.accessToken;
                // console.log(accessToken)
            } catch (error) {
                console.warn(`Unable to acquire token silently: ${error}`);
            }
            if (accessToken === null) {
                // Acquire token silent failure. Send an interactive request via popup.
                try {
                    // console.log("Trying to acquire token interactively...");
                    const userAccount = await nestablePublicClientApplication.acquireTokenPopup(tokenRequest);
                    // console.log("Acquired token interactively.");
                    accessToken = userAccount.accessToken;
                } catch (popupError) {
                    // Acquire token interactive failure.
                    console.warn(`Unable to acquire token interactively: ${popupError}`);
                }
            }
            // Log error if both silent and popup requests failed.
            if (accessToken === null) {
                console.error(`Unable to acquire access token.`);
                return;
            }
        }

        return accessToken;
    } else {
        return await Office.auth.getAccessToken();
    }
}

export async function getAccessToken() {
    let accessToken: string = "";
    try {
        accessToken = await Office.auth.getAccessToken();
    } catch (error) {
        console.error("ERROR: Could not get SSO access token 1", error)
    }

    if (!accessToken)
        try {
            accessToken = await Office.auth.getAccessToken({allowSignInPrompt: true});
        } catch (error) {
            console.error("ERROR: Could not get SSO access token 2", error)
        }

    if (!accessToken)
        try {
            accessToken = await OfficeRuntime.auth.getAccessToken();
        } catch (error) {
            console.error("ERROR: Could not get SSO access token 3", error)
        }

    if (!accessToken)
        try {
            accessToken = await OfficeRuntime.auth.getAccessToken({allowSignInPrompt: true});
        } catch (error) {
            console.error("ERROR: Could not get SSO access token 4", error)
        }

    if (!accessToken){
        const msalInstance = new PublicClientApplication({
            auth: {
                clientId: 'cdd317cf-41a2-4f00-af69-77e4aa7219d2',
                authority: 'https://login.microsoftonline.com/4e61e794-0ccf-4725-80e9-8bbf56f17e3c',
                redirectUri: 'https://localhost:3000/login/login.html' // Must be registered as "spa" type
            },
            cache: {
                cacheLocation: 'localStorage', // needed to avoid "login required" error
                storeAuthStateInCookie: true   // recommended to avoid certain IE/Edge issues
            }
        });

        await msalInstance.initialize();

        try {
            const result = await msalInstance.ssoSilent({scopes: ['openid', 'profile']});
            // console.log(result);
            accessToken = result.accessToken;
            return accessToken;
        }catch (error){
            console.error(error);
        }
    }


    return accessToken;

}